import { useRef, useState, useMemo, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@chakra-ui/react'
import { Search } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { AdminLayout } from '@/components/layout/AdminLayout'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { ActionButtons } from '@/components/common/ActionButtons'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { useAccessCheck } from '@/hooks/useAccess'
import { exportToCSV } from '@/lib/utils/csvUtils'
import { apiClient } from '@/lib/api/client'
import { useThemeColors } from '@/hooks/useThemeColors'

type TabValue = 'all' | 'trash'

export default function DepartmentsPage() {
  const colors = useThemeColors()
  const navigate = useNavigate()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isExporting, setIsExporting] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const access = useAccessCheck()

  const statusFilterOptions = useMemo(
    () => [
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
    ],
    []
  )

  const dataSource =
    activeTab === 'trash' ? '/organization/archived/departments' : '/organization/departments'

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      dataFields: {
        name: { name: 'Name', withSort: true, maxWidth: '200px' },
        slug: { name: 'Slug', withSort: true, maxWidth: '200px' },
        department_code: { name: 'Code', withSort: true, maxWidth: '120px' },
        parent_department_name: { name: 'Parent', withSort: false, maxWidth: '180px' },
        active: {
          name: 'Status',
          withSort: true,
          withFilter: true,
          filterOptions: statusFilterOptions,
          badge: { Active: 'green', Inactive: 'yellow' },
          badgeLabels: { Active: 'Active', Inactive: 'Inactive' },
        },
        updated_at: { name: 'Updated At', withSort: true, maxWidth: '150px' },
      },
      dataSource,
      primaryKey: 'id',
      softDelete: true,
      displayInModal: false,
      noRecordText: 'No departments found',
    }),
    [activeTab, dataSource, statusFilterOptions]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(() => ({ search: searchQuery }), [searchQuery])

  const showSubSubCallback = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    if (searchRef.current) searchRef.current.value = ''
    tableRef.current?.clearPage()
  }

  const handleSearch = () => {
    const q = searchRef.current?.value || ''
    setSearchQuery(q)
    tableRef.current?.applyFilter('search', q)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const ids = tableRef.current?.getSelectedRows() || []
    if (ids.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setModalMessage(
      bulkAction === 'delete' && activeTab === 'trash'
        ? 'You are about to permanently delete these items. This cannot be undone.'
        : 'Are you sure you want to apply this change?'
    )
    onOpen()
  }

  const handleConfirmBulkAction = async () => {
    const ids = tableRef.current?.getSelectedRows() || []
    if (ids.length === 0) return
    setIsBulkProcessing(true)
    try {
      let url: string
      let msg: string
      if (bulkAction === 'restore') {
        url = '/organization/departments/bulk/restore'
        msg = 'Departments restored successfully'
      } else if (bulkAction === 'delete') {
        url =
          activeTab === 'trash'
            ? '/organization/departments/bulk/force-delete'
            : '/organization/departments/bulk/delete'
        msg =
          activeTab === 'trash' ? 'Departments permanently deleted' : 'Departments moved to trash'
      } else {
        return
      }
      await apiClient.post(url, { ids })
      showSuccess('Success', msg)
      tableRef.current?.reload()
      setBulkAction('')
      onClose()
    } catch (e: any) {
      showError(e)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  const handleExportData = async () => {
    setIsExporting(true)
    try {
      await exportToCSV({
        endpoint: activeTab === 'trash' ? '/organization/archived/departments' : '/organization/departments',
        selectedIds: [],
        activeTab,
        searchQuery: '',
        csvHeaders: [
          'ID',
          'Name',
          'Slug',
          'Department Code',
          'Parent ID',
          'Descriptions',
          'Active',
          'Created At',
          'Updated At',
          'Deleted At',
        ],
        dataMapper: (d: any) => [
          d.id ?? '',
          d.name ?? '',
          d.slug ?? '',
          d.department_code ?? '',
          d.parent_id ?? '',
          d.descriptions ?? '',
          d.active === 'Active' ? 'Yes' : 'No',
          d.created_at ?? '',
          d.updated_at ?? '',
          d.deleted_at ?? '',
        ],
        filename: `departments_export_${activeTab === 'trash' ? 'trash_' : ''}${new Date().toISOString().split('T')[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      })
    } catch (e: any) {
      showError(e)
    } finally {
      setIsExporting(false)
    }
  }

  const handleDownloadTemplate = async () => {
    try {
      const res = await apiClient.get('/organization/departments/download-template', {
        responseType: 'blob',
      })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = 'departments_template.csv'
      a.click()
      URL.revokeObjectURL(url)
      showSuccess('Success', 'Template downloaded')
    } catch (e: any) {
      showError(e)
    }
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsImporting(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const { data } = await apiClient.post<{ data: { imported: number; errors: string[] } }>(
        '/organization/departments/import',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      const d = data?.data ?? { imported: 0, errors: [] }
      showSuccess('Success', `Imported ${d.imported} department(s)`)
      if (d.errors?.length) {
        showWarning(d.errors.slice(0, 3).join('; ') + (d.errors.length > 3 ? '...' : ''))
      }
      tableRef.current?.reload()
    } catch (err: any) {
      showError(err)
    } finally {
      setIsImporting(false)
      e.target.value = ''
    }
  }

  return (
    <AdminLayout>
      <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
        <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
            <CardHeader flexShrink={0} py={3}>
              <Heading size="md" mb={1}>
                Departments
              </Heading>
              <Text fontSize="sm" color={colors.text}>
                Manage organizational departments and hierarchy
              </Text>
            </CardHeader>

            <CardHeader py={0} px={0} flexShrink={0}>
              <Box pt={2} pb={0}>
                <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                  <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                    <TabList borderBottom="none">
                      <Tab>All <Badge ml={2} colorScheme="blue">{totalRows}</Badge></Tab>
                      {totalTrash > 0 && (
                        <Tab>Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge></Tab>
                      )}
                    </TabList>
                  </Tabs>
                  <Box pb={2}>
                    <ActionButtons
                      pathname="/organization/departments"
                      onExport={handleExportData}
                      onDownloadTemplate={handleDownloadTemplate}
                      createLabel="Create New Department"
                      isExporting={isExporting}
                      isImporting={isImporting}
                      fileInputRef={fileInputRef}
                      onFileUpload={handleFileUpload}
                    />
                  </Box>
                </HStack>
                <Box borderBottom="2px" borderColor={colors.border} mx={4} />
              </Box>
            </CardHeader>

            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}
                <Box flex="0 1 auto" minW="250px" maxW="400px">
                  <InputGroup size="sm">
                    <Input
                      ref={searchRef}
                      placeholder="Search by name, slug, or code..."
                      onKeyDown={handleKeyDown}
                      defaultValue={searchQuery}
                      pr="2.5rem"
                    />
                    <InputRightElement width="2.5rem">
                      <IconButton
                        aria-label="Search"
                        icon={<Search size={16} />}
                        size="sm"
                        h="1.75rem"
                        w="1.75rem"
                        onClick={handleSearch}
                        variant="ghost"
                      />
                    </InputRightElement>
                  </InputGroup>
                </Box>
              </HStack>
            </CardHeader>

            <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
              <Box h="100%" display="flex" flexDirection="column" minH={0}>
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  access={permissions}
                  setSubSub={showSubSubCallback}
                  onOpenModal={(id) => navigate(`/organization/departments/${id}`)}
                  filterOptions={{ active: statusFilterOptions }}
                />
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody><Text>{modalMessage}</Text></ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  )
}
